import React, { useState } from 'react';
import { 
  LayoutDashboard, Database, Users, Building2, Briefcase, Package, Calendar, 
  Settings, ChevronDown, ChevronRight, Search, Bell, LogOut, Menu, X,
  MapPin, Globe, Crown, Tag, ShoppingBag, BookOpen, Layers, FileText,
  BarChart3, GitBranch, Shield, Clock, Truck, Home, CalendarDays, Key,
  Server, Code, Braces, List, Hash, FolderTree
} from 'lucide-react';

// Struttura del menu amministrativo
const ADMIN_MENU = [
  {
    id: 'overview',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: 'overview',
  },
  {
    id: 'database',
    label: 'Database',
    icon: Database,
    submenu: [
      { id: 'schema', label: 'Schema Completo', icon: GitBranch, path: 'schema' },
      { id: 'explorer', label: 'Data Explorer', icon: Search, path: 'explorer' },
      { id: 'relations', label: 'Relazioni Entità', icon: Braces, path: 'relations' },
      { id: 'sitemap', label: 'Site Map', icon: FolderTree, path: 'sitemap' },
    ],
  },
  {
    id: 'entities',
    label: 'Entità',
    icon: Server,
    submenu: [
      { id: 'companies', label: 'Aziende', icon: Building2, path: 'companies' },
      { id: 'offices', label: 'Sedi (Office)', icon: MapPin, path: 'offices' },
      { id: 'users', label: 'Utenti', icon: Users, path: 'users' },
      { id: 'resources', label: 'Risorse', icon: Briefcase, path: 'resources' },
      { id: 'categories', label: 'Categorie', icon: Tag, path: 'categories' },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalogo',
    icon: Package,
    submenu: [
      { id: 'services', label: 'Servizi Orari', icon: Clock, path: 'services' },
      { id: 'rentals', label: 'Noleggi', icon: Key, path: 'rentals' },
      { id: 'products', label: 'Prodotti', icon: Package, path: 'products' },
      { id: 'events', label: 'Eventi', icon: Calendar, path: 'events' },
      { id: 'courses', label: 'Corsi', icon: BookOpen, path: 'courses' },
    ],
  },
  {
    id: 'transactions',
    label: 'Transazioni',
    icon: ShoppingBag,
    submenu: [
      { id: 'bookings', label: 'Prenotazioni', icon: CalendarDays, path: 'bookings' },
      { id: 'orders', label: 'Ordini', icon: Truck, path: 'orders' },
    ],
  },
  {
    id: 'modules',
    label: 'Sistema Modulare',
    icon: Layers,
    submenu: [
      { id: 'modules-config', label: 'Configurazione Moduli', icon: Settings, path: 'modules-config' },
      { id: 'modules-assign', label: 'Assegna Moduli', icon: Building2, path: 'modules-assign' },
    ],
  },
  {
    id: 'geography',
    label: 'Geografia',
    icon: Globe,
    submenu: [
      { id: 'communities', label: 'Community', icon: Users, path: 'communities' },
      { id: 'locations', label: 'Locations', icon: MapPin, path: 'locations' },
    ],
  },
  {
    id: 'programs',
    label: 'Programmi Fedeltà',
    icon: Crown,
    submenu: [
      { id: 'star-program', label: 'Programma Star', icon: Crown, path: 'star-program' },
      { id: 'elit-program', label: 'Programma Elit', icon: Shield, path: 'elit-program' },
    ],
  },
  {
    id: 'config',
    label: 'Configurazione',
    icon: Settings,
    submenu: [
      { id: 'statuses', label: 'Stati', icon: Hash, path: 'statuses' },
      { id: 'platform', label: 'Piattaforma', icon: Server, path: 'platform' },
    ],
  },
];

const AdminLayout = ({ children, currentSection, onSectionChange, onLogout }) => {
  const [expandedMenus, setExpandedMenus] = useState(['database', 'entities', 'modules']);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const handleNavigate = (path) => {
    onSectionChange(path);
    setMobileMenuOpen(false);
  };

  const renderMenuItem = (item, level = 0) => {
    const Icon = item.icon;
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isExpanded = expandedMenus.includes(item.id);
    const isActive = currentSection === item.path || 
      (hasSubmenu && item.submenu.some(sub => sub.path === currentSection));

    return (
      <div key={item.id}>
        <button
          onClick={() => {
            if (hasSubmenu) {
              toggleMenu(item.id);
            } else {
              handleNavigate(item.path);
            }
          }}
          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors rounded-lg mx-2 mb-1 ${
            isActive 
              ? 'bg-emerald-50 text-emerald-700' 
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          } ${level > 0 ? 'ml-4' : ''}`}
        >
          <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-emerald-600' : 'text-gray-400'}`} />
          {!sidebarCollapsed && (
            <>
              <span className="flex-1 font-medium text-sm">{item.label}</span>
              {hasSubmenu && (
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              )}
            </>
          )}
        </button>
        
        {/* Submenu */}
        {hasSubmenu && isExpanded && !sidebarCollapsed && (
          <div className="ml-4 border-l-2 border-gray-100 pl-2">
            {item.submenu.map(subItem => {
              const SubIcon = subItem.icon;
              const isSubActive = currentSection === subItem.path;
              return (
                <button
                  key={subItem.id}
                  onClick={() => handleNavigate(subItem.path)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors rounded-lg mx-2 mb-0.5 ${
                    isSubActive 
                      ? 'bg-emerald-50 text-emerald-700' 
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  }`}
                >
                  <SubIcon className={`w-4 h-4 flex-shrink-0 ${isSubActive ? 'text-emerald-500' : 'text-gray-400'}`} />
                  <span className="text-sm">{subItem.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        bg-white border-r border-gray-200 
        transition-all duration-300 flex flex-col
        ${sidebarCollapsed ? 'w-20' : 'w-72'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900">Skipres</h1>
                <p className="text-xs text-gray-500">Admin Panel</p>
              </div>
            </div>
          )}
          <button 
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 hover:bg-gray-100 rounded-lg hidden lg:block"
          >
            <Menu className="w-5 h-5 text-gray-500" />
          </button>
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {ADMIN_MENU.map(item => renderMenuItem(item))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors rounded-lg"
          >
            <LogOut className="w-5 h-5" />
            {!sidebarCollapsed && <span className="font-medium text-sm">Esci</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca nel database..."
                className="w-80 pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="p-2 hover:bg-gray-100 rounded-lg relative">
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <div className="flex items-center gap-2 pl-3 border-l border-gray-200">
              <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">A</span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-900">Admin</p>
                <p className="text-xs text-gray-500">Super Admin</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
export { ADMIN_MENU };
